/**
 * One entry per address, holding the communications it is subscribed to. Returned sorted by
 * address so the cards keep a stable order across refetches, which the queries do not guarantee.
 */
export const groupCommunicationsByEmail = (rows) => {
    const byEmail = new Map();
    if (!Array.isArray(rows)) return [];

    for (const row of rows) {
        if (!row?.communication_id || !row.email) continue;

        const group = byEmail.get(row.email);
        if (!group) {
            byEmail.set(row.email, { email: row.email, communicationIds: [row.communication_id] });
        } else if (!group.communicationIds.includes(row.communication_id)) {
            group.communicationIds.push(row.communication_id);
        }
    }

    return [...byEmail.values()].sort((a, b) => a.email.localeCompare(b.email));
};

// Used for both directions of the many-to-many: diffing an address's communications, and diffing
// a communication's addresses. Prefer diffVisibleCommunications when `current` comes from the
// server and `next` comes from a picker — see the comment there.
export const diffSets = (current, next) => {
    const currentSet = new Set(current);
    const nextSet = new Set(next);

    const remove = [];
    for (const member of currentSet) {
        if (!nextSet.has(member)) remove.push(member);
    }

    const add = [];
    for (const member of nextSet) {
        if (!currentSet.has(member)) add.push(member);
    }

    return { remove, add };
};

/**
 * A subscription row can point at a communication the picker never shows — ADMIN_* types are
 * filtered out by buildCommSections, and a row can outlive its email_communications entry. Those
 * ids are absent from `nextIds` for the simple reason that the user was never offered them, so a
 * plain diffSets would report them as removals and silently unsubscribe the address from
 * notifications it cannot see. Only ids the picker can actually represent are diffed; the rest are
 * left alone.
 */
export const diffVisibleCommunications = (currentIds, nextIds, commsById) => {
    const visible = (currentIds || []).filter((id) => commsById?.has(id));

    return diffSets(visible, nextIds || []);
};

// Word-per-word title casing turns 'ltl' into 'Ltl'. These are the only acronyms that appear
// anywhere in the order_events enum, so the map is exhaustive rather than a guess at what might
// show up — 'w' is left out because its one event (email:w_form_updated:admin) is ADMIN_CS and
// never reaches the client UI.
const ACRONYMS = { ltl: 'LTL', tms: 'TMS' };

// email_communications.name and .description default to '' and are empty for every row today, so
// the label is derived from the event enum: 'email:carrier_received:shipper' -> 'Carrier Received'.
// The trailing audience is dropped because it is already the section heading. Callers still prefer
// `name` when it is set, so populating the column later takes precedence without a code change.
export const communicationLabel = (event) => {
    if (typeof event !== 'string' || !event) return '';

    const parts = event.split(':');
    const subject = parts.length > 2 ? parts.slice(1, -1).join(':') : parts[parts.length - 1];

    return subject
        .split('_')
        .filter(Boolean)
        .map((word) => ACRONYMS[word.toLowerCase()] || word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const SECTION_ORDER = ['SHIPPER', 'CARRIER', 'CUSTOMER'];
const SECTION_TITLES = { SHIPPER: 'Shipper', CARRIER: 'Carrier', CUSTOMER: 'Customer' };

/**
 * Sections derived from email_communications.type. ADMIN_* types are excluded — those notify
 * Onward staff, not clients. Anything with an unrecognised type is dropped rather than guessed.
 */
export const buildCommSections = (comms) => {
    const byType = new Map(SECTION_ORDER.map((type) => [type, []]));

    for (const comm of Array.isArray(comms) ? comms : []) {
        const bucket = byType.get(comm?.type);
        if (bucket) bucket.push({ ...comm, label: communicationLabel(comm.event) });
    }

    return SECTION_ORDER.reduce((acc, type) => {
        const options = byType.get(type);
        if (!options.length) return acc;

        options.sort((a, b) => a.label.localeCompare(b.label));
        acc.push({ section: SECTION_TITLES[type], options });
        return acc;
    }, []);
};

// Flat list for a picker that groups by itself, so each option carries its section heading.
export const flattenCommSections = (commSections) =>
    (commSections || []).reduce((acc, { section, options }) => {
        for (const comm of options) acc.push({ ...comm, section });
        return acc;
    }, []);

/**
 * The communications an address is subscribed to, as option objects for the picker. Ids with no
 * matching communication are dropped — a row can outlive its email_communications entry.
 */
export const selectedCommOptions = (communicationIds, commsById) =>
    (communicationIds || []).reduce((acc, id) => {
        const comm = commsById.get(id);
        if (comm) acc.push(comm);
        return acc;
    }, []);
